<template>
  <div class="apps-gallery">
    <div v-for="category in filteredCategories" class="apps-gallery__container" :key="category.key">
      <span class="apps-gallery__category">{{ displayCategory(category.key) }}</span>

      <div class="apps-gallery__list">
        <mini-app-card
          v-for="app in category.items"
          @increment="$emit('increment', app)"
          @decrement="$emit('decrement', app)"
          :key="app.key"
          :app="app"
          :insertion-scope="appsInserted"
        />
      </div>
    </div>

    <div v-if="filteredCollectionIsEmpty" class="apps-gallery__empty">
      <span>{{ $t("WorkspaceEdit.addApps.labels.empty") }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { CategoryItem, InsertedAppRegistry } from "@/views/workspaceEdit/lib/types"
import { defineComponent, computed, PropType } from "@vue/composition-api"
import { Dictionary } from "vue-router/types/router"
import { Filter } from "./utils/types"
import MiniAppCard from "./MiniAppCard.vue"
import { AppItem } from "@/types/workspace"

export default defineComponent({
  name: "AppsGallery",
  props: {
    filter: {
      type: Object as PropType<Filter>,
      required: true
    },
    appsInserted: {
      type: Object as PropType<Dictionary<InsertedAppRegistry>>,
      required: true
    },
    appCategoryItemMap: {
      type: Object as PropType<Dictionary<CategoryItem<AppItem>>>,
      required: true
    }
  },
  setup(props) {
    const filteredCategories = computed(() => {
      const appsInCategories = props.appCategoryItemMap

      if (props.filter.tags.length > 0) {
        return _filterTags()
      }

      if (props.filter.search.length > 0) {
        return _filterSnippet()
      }

      return appsInCategories
    })

    const filteredCollectionIsEmpty = computed(() => {
      return Object.values(filteredCategories.value).length === 0
    })

    const displayCategory = (categoryKey: string) => {
      return categoryKey.replace(/[-]/g, " ")
    }

    const _filterTags = () => {
      const tags = props.filter.tags
      const filter = {} as Dictionary<CategoryItem<AppItem>>
      const appsInCategories = props.appCategoryItemMap

      if (tags.length) {
        for (const key in appsInCategories) {
          const category = appsInCategories[key]

          if (category && tags.includes(category.key)) {
            filter[key] = category
          }
        }
      }

      return filter
    }

    const _filterSnippet = () => {
      const filter = {} as Dictionary<CategoryItem<AppItem>>
      const snippet = props.filter.search
      const appsInCategories = props.appCategoryItemMap

      for (const key in appsInCategories) {
        const category = appsInCategories[key]

        if (category) {
          const matchesText = category.items.some(app => _compareSnippet(app.text, snippet))

          if (matchesText) {
            filter[key] = category
          }
        }
      }

      return filter
    }

    const _compareSnippet = (textOne: string, textTwo: string) => {
      return textOne.toLowerCase().includes(textTwo.toLowerCase())
    }

    return {
      filteredCollectionIsEmpty,
      filteredCategories,
      displayCategory
    }
  },
  components: {
    MiniAppCard
  }
})
</script>
